import {
  doc,
  getDoc,
  collection,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  writeBatch
} from 'firebase/firestore'
import { setPersistence, browserLocalPersistence } from 'firebase/auth'
import { db, auth } from './firebase'

let persistenceConfigured = false

async function ensureCertificationPersistence() {
  if (typeof window === 'undefined' || persistenceConfigured) return
  persistenceConfigured = true
  try {
    await setPersistence(auth, browserLocalPersistence)
  } catch {
    // non-fatal
  }
}

function requireSignedInUser() {
  const user = auth.currentUser
  if (!user) {
    const err = new Error('NOT_SIGNED_IN')
    throw err
  }
  return user
}

function trackDocRef(uid, trackId) {
  return doc(db, 'certificationTracks', `${uid}_${trackId}`)
}

/** For public verification matching (case-insensitive, collapsed whitespace). */
function normalizeDisplayNameNorm(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

/**
 * Latest snapshot + free attempt flag for one track (per authenticated user).
 */
export async function fetchCertificationTrackDoc(trackId) {
  await ensureCertificationPersistence()
  const user = requireSignedInUser()
  const ref = trackDocRef(user.uid, trackId)
  const snap = await getDoc(ref)
  return { user, ref, snap }
}

/**
 * Writes track summary and appends an immutable history row for "My results".
 */
export async function persistCertificationSubmit(trackId, payload) {
  await ensureCertificationPersistence()
  const user = requireSignedInUser()
  const ref = trackDocRef(user.uid, trackId)
  const {
    resultPayload,
    certificateId,
    completedDateLabel,
    displayName,
    participantPhone,
    participantEmail,
    participantOrganisation,
    quizTitle,
    trackLabel,
    scoreText,
    completedDateIso
  } = payload

  const trackPayload = {
    trackId,
    freeUsed: true,
    resultJson: JSON.stringify(resultPayload),
    certificateId: certificateId || '',
    completedDateLabel: completedDateLabel || '',
    displayName: displayName || '',
    participantPhone: participantPhone || '',
    participantEmail: participantEmail || '',
    participantOrganisation: participantOrganisation || '',
    quizTitle: quizTitle || '',
    trackLabel: trackLabel || '',
    scoreText: scoreText || '',
    rawScore: resultPayload.rawScore,
    passed: resultPayload.passed,
    correct: resultPayload.correct,
    updatedAt: serverTimestamp()
  }

  const attemptRef = doc(collection(db, 'certificationAttempts'))
  const attemptPayload = {
    userId: user.uid,
    authPhone: user.phoneNumber || '',
    trackId,
    trackLabel: trackLabel || '',
    quizTitle: quizTitle || '',
    resultJson: JSON.stringify(resultPayload),
    certificateId: certificateId || '',
    completedDateLabel: completedDateLabel || '',
    displayName: displayName || '',
    participantPhone: participantPhone || '',
    participantEmail: participantEmail || '',
    participantOrganisation: participantOrganisation || '',
    rawScore: resultPayload.rawScore,
    maxPoints: resultPayload.maxPoints,
    passPoints: resultPayload.passPoints,
    passed: resultPayload.passed,
    correct: resultPayload.correct,
    total: resultPayload.total,
    scoreText: scoreText || '',
    createdAt: serverTimestamp()
  }

  const batch = writeBatch(db)
  batch.set(ref, trackPayload, { merge: true })
  batch.set(attemptRef, attemptPayload)

  if (resultPayload.passed === true && certificateId) {
    const publicRef = doc(db, 'certificatePublicRecords', certificateId)
    batch.set(
      publicRef,
      {
        displayName: displayName || '',
        displayNameNorm: normalizeDisplayNameNorm(displayName),
        completedDateLabel: completedDateLabel || '',
        completedDateIso: String(completedDateIso || '').slice(0, 10),
        quizTitle: quizTitle || '',
        trackLabel: trackLabel || '',
        passed: true,
        createdAt: serverTimestamp()
      },
      { merge: true }
    )
  }

  await batch.commit()
}

/**
 * Past attempts for the signed-in user (newest first).
 */
export async function fetchCertificationAttemptsList(maxRows = 40) {
  await ensureCertificationPersistence()
  const user = requireSignedInUser()
  const q = query(
    collection(db, 'certificationAttempts'),
    where('userId', '==', user.uid),
    orderBy('createdAt', 'desc'),
    limit(maxRows)
  )
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
}

/**
 * Latest attempt row for a track (scan recent history; same index as list query).
 */
export async function fetchLatestCertificationAttemptForTrack(trackId, maxScan = 60) {
  const rows = await fetchCertificationAttemptsList(maxScan)
  return rows.find((r) => r.trackId === trackId) ?? null
}
